import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // Both list flags start true: the page mounts before its first fetch is
  // even dispatched, and a false here would paint an empty backlog / zeroed
  // KPIs for that frame — read them as "not fetched yet", not "idle".
  loading: true,
  saving: false,

  boards: [],
  currentBoardId: null,

  // GET /service-request/board/:boardId/kanban -> { columns, cards: { [columnKey]: [] } }
  kanban: { columns: [], cards: {} },
  kanbanLoading: false,

  templates: [],
  issueCategories: [],

  // GET /service-request (backlog list)
  requests: [],
  requestsTotal: 0,
  counts: { backlog: 0, ready: 0, in_progress: 0, awaiting_approval: 0, approved: 0, rejected: 0, all: 0 },

  dashboardKpis: { openRequests: 0, inReview: 0, awaitingApproval: 0, approvedThisMonth: 0, myQueueCount: 0 },
  kpisLoading: true,

  staffRequests: [],
  staffCounts: { active: 0, completed: 0 },

  selectedRequest: null,
  selectedRequestLoading: false,

  // GET/PATCH /service-request/settings — General + Notifications, one
  // record per business. Defaults mirror what the backend returns when a
  // business has no settings doc saved yet.
  settings: {
    requestNumberPrefix: "SR",
    requestSequence: 0,
    nextRequestNumber: "SR-000001",
    allowRequesterCancel: true,
    emailNewRequest: true,
    emailDecision: true,
    emailResolved: true,
    dailyDigest: false,
    digestTime: "8:00 AM",
    escalate: true,
    escalateDays: 2,
  },
  settingsLoading: false,
  settingsSaving: false,
};

export const serviceRequestsState = createSlice({
  name: "serviceRequests",
  initialState,
  reducers: {
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setSaving(state, action) {
      state.saving = action.payload;
    },
    setBoards(state, action) {
      state.boards = action.payload || [];
      if (!state.currentBoardId && state.boards.length) {
        state.currentBoardId = (state.boards.find((b) => b.isDefault) || state.boards[0]).id;
      }
    },
    setCurrentBoardId(state, action) {
      state.currentBoardId = action.payload;
    },
    setKanbanLoading(state, action) {
      state.kanbanLoading = action.payload;
    },
    setKanban(state, action) {
      state.kanbanLoading = false;
      state.kanban = action.payload || { columns: [], cards: {} };
    },
    moveCardLocal(state, action) {
      // Optimistic client-side move between kanban columns, ahead of the
      // PATCH /:id/move response — rolled back via setKanban on failure.
      const { id, fromColumnKey, toColumnKey } = action.payload;
      const cards = state.kanban.cards || {};
      const fromList = cards[fromColumnKey] || [];
      const card = fromList.find((r) => r.id === id);
      if (!card) return;
      cards[fromColumnKey] = fromList.filter((r) => r.id !== id);
      cards[toColumnKey] = [...(cards[toColumnKey] || []), { ...card, columnKey: toColumnKey }];
    },
    setTemplates(state, action) {
      state.templates = action.payload || [];
    },
    setIssueCategories(state, action) {
      state.issueCategories = action.payload || [];
    },
    setRequests(state, action) {
      state.loading = false;
      state.requests = action.payload?.data || [];
      state.requestsTotal = action.payload?.total ?? state.requests.length;
    },
    setCounts(state, action) {
      state.counts = { ...state.counts, ...action.payload };
    },
    setKpisLoading(state, action) {
      state.kpisLoading = action.payload;
    },
    setDashboardKpis(state, action) {
      state.kpisLoading = false;
      state.dashboardKpis = action.payload || state.dashboardKpis;
    },
    setStaffRequests(state, action) {
      state.staffRequests = action.payload || [];
    },
    setStaffCounts(state, action) {
      state.staffCounts = action.payload || state.staffCounts;
    },
    setSelectedRequestLoading(state, action) {
      state.selectedRequestLoading = action.payload;
    },
    setSelectedRequest(state, action) {
      state.selectedRequestLoading = false;
      state.selectedRequest = action.payload;
    },
    patchRequestInLists(state, action) {
      // Keep the backlog list + selected-request detail in sync after a
      // PATCH/approve/reject/assign, without a full refetch.
      const { id, patch } = action.payload;
      const idx = state.requests.findIndex((r) => r.id === id);
      if (idx !== -1) state.requests[idx] = { ...state.requests[idx], ...patch };
      if (state.selectedRequest?.id === id) {
        state.selectedRequest = { ...state.selectedRequest, ...patch };
      }
    },
    removeRequestFromLists(state, action) {
      state.requests = state.requests.filter((r) => r.id !== action.payload);
    },
    reorderRequests(state, action) {
      // Backlog drag-and-drop drops a row at a precise position, not just into
      // a stage. Nothing on the server records that position — ServiceRequest
      // has no order field — so this is the visible half of the gesture and it
      // lasts until the next fetch; the stage change beside it does persist.
      const { id, beforeId } = action.payload;
      const from = state.requests.findIndex((r) => r.id === id);
      if (from < 0) return;
      const [row] = state.requests.splice(from, 1);
      const to = beforeId ? state.requests.findIndex((r) => r.id === beforeId) : -1;
      state.requests.splice(to < 0 ? state.requests.length : to, 0, row);
    },
    setSettingsLoading(state, action) {
      state.settingsLoading = action.payload;
    },
    setSettingsSaving(state, action) {
      state.settingsSaving = action.payload;
    },
    setSettings(state, action) {
      state.settingsLoading = false;
      state.settings = { ...state.settings, ...action.payload };
    },
    reset() {
      return initialState;
    },
  },
});

export const {
  setLoading,
  setSaving,
  setBoards,
  setCurrentBoardId,
  setKanbanLoading,
  setKanban,
  moveCardLocal,
  setTemplates,
  setIssueCategories,
  setRequests,
  setCounts,
  setKpisLoading,
  setDashboardKpis,
  setStaffRequests,
  setStaffCounts,
  setSelectedRequestLoading,
  setSelectedRequest,
  patchRequestInLists,
  removeRequestFromLists,
  reorderRequests,
  setSettingsLoading,
  setSettingsSaving,
  setSettings,
  reset,
} = serviceRequestsState.actions;
export default serviceRequestsState.reducer;
